import { Injectable } from '@angular/core';
import { ApiClient } from '../http/api-client';
import { ScheduledReportPreview, ScheduledReportPreviewOverrides, ScheduledReportRunResult } from '../scheduled-reports/scheduled-reports.service';

export type PayrollDashboardStatus = 'draft' | 'active' | 'archived';
export type PayrollDashboardMetricType = 'count' | 'sum' | 'average' | 'minimum' | 'maximum';
export type PayrollDashboardValueFormat = 'number' | 'currency' | 'percent';
export type PayrollDashboardActionTargetType = 'saved_report' | 'report_template';

export interface PayrollDashboardTileDefinition {
  id: string;
  title: string;
  description?: string;
  reportId: string;
  metricType: PayrollDashboardMetricType;
  fieldKey?: string;
  valueFormat: PayrollDashboardValueFormat;
  precision: number;
}

export interface PayrollDashboardActionDefinition {
  id: string;
  label: string;
  description?: string;
  targetType: PayrollDashboardActionTargetType;
  reportId?: string;
  templateKey?: string;
}

export interface PayrollDashboardDefinition {
  id: string;
  name: string;
  description?: string;
  status: PayrollDashboardStatus;
  tiles: PayrollDashboardTileDefinition[];
  actions: PayrollDashboardActionDefinition[];
  metadata?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  permissionCount?: number;
}

export type PayrollDashboardPermissionSubjectType = 'user' | 'company' | 'team' | 'brand';

export interface PayrollDashboardPermission {
  id?: string;
  dashboardId?: string;
  subjectType: PayrollDashboardPermissionSubjectType;
  subjectId: string;
}

export interface PayrollDashboardPermissionsResponse {
  dashboardId: string;
  permissions: PayrollDashboardPermission[];
}

@Injectable({
  providedIn: 'root',
})
export class PayrollDashboardsService {
  private readonly basePath = 'payroll/dashboards';

  constructor(private restangular: ApiClient) {}

  async listDashboards(status?: PayrollDashboardStatus): Promise<PayrollDashboardDefinition[]> {
    try {
      const response = await this.restangular
        .one(this.basePath)
        .customGET('', status ? { status } : {})
        .toPromise();
      return this.extractDashboards(response);
    } catch {
      return [];
    }
  }

  async listAllowedDashboards(): Promise<PayrollDashboardDefinition[]> {
    try {
      const response = await this.restangular.one(this.basePath).one('allowed').customGET().toPromise();
      return this.extractDashboards(response);
    } catch {
      return [];
    }
  }

  async upsertDashboard(definition: PayrollDashboardDefinition, isExisting = false): Promise<PayrollDashboardDefinition> {
    const normalized = this.normalizeDashboard(definition);
    const response = isExisting
      ? await this.restangular.one(this.basePath, normalized.id).customPUT(this.toDashboardUpdatePayload(normalized)).toPromise()
      : await this.restangular.one(this.basePath).customPOST(this.toDashboardPayload(normalized)).toPromise();

    return this.toDashboardDefinition(response) || normalized;
  }

  async deleteDashboard(id: string): Promise<void> {
    await this.restangular.one(this.basePath, id).customDELETE().toPromise();
  }

  async getPermissions(dashboardId: string): Promise<PayrollDashboardPermissionsResponse> {
    const response = await this.restangular.one(this.basePath, dashboardId).one('permissions').customGET().toPromise();
    return {
      dashboardId: `${response?.dashboardId || dashboardId}`,
      permissions: Array.isArray(response?.permissions)
        ? response.permissions.map((permission: unknown) => this.toPermission(permission)).filter(Boolean) as PayrollDashboardPermission[]
        : [],
    };
  }

  async replacePermissions(dashboardId: string, permissions: PayrollDashboardPermission[]): Promise<PayrollDashboardPermissionsResponse> {
    const payload = {
      permissions: permissions
        .map((permission) => this.toPermissionPayload(permission))
        .filter(Boolean),
    };
    const response = await this.restangular.one(this.basePath, dashboardId).one('permissions').customPUT(payload).toPromise();
    return {
      dashboardId: `${response?.dashboardId || dashboardId}`,
      permissions: Array.isArray(response?.permissions)
        ? response.permissions.map((permission: unknown) => this.toPermission(permission)).filter(Boolean) as PayrollDashboardPermission[]
        : [],
    };
  }

  async addPermission(dashboardId: string, permission: PayrollDashboardPermission): Promise<PayrollDashboardPermission> {
    const response = await this.restangular
      .one(this.basePath, dashboardId)
      .one('permissions')
      .customPOST(this.toPermissionPayload(permission))
      .toPromise();
    return this.toPermission(response) || permission;
  }

  async deletePermission(dashboardId: string, permissionId: string): Promise<void> {
    await this.restangular.one(this.basePath, dashboardId).one('permissions', permissionId).customDELETE().toPromise();
  }

  async previewDashboardReport(dashboardId: string, reportId: string, limit = 25, overrides?: ScheduledReportPreviewOverrides): Promise<ScheduledReportPreview> {
    return this.restangular
      .one(this.basePath, dashboardId)
      .one('reports', reportId)
      .one('preview')
      .customPOST(overrides || {}, '', { limit })
      .toPromise();
  }

  async downloadDashboardReport(dashboardId: string, reportId: string, overrides?: ScheduledReportPreviewOverrides): Promise<Blob> {
    return this.restangular
      .one(this.basePath, dashboardId)
      .one('reports', reportId)
      .one('download')
      .withHttpConfig({ responseType: 'blob' })
      .customPOST(overrides || {})
      .toPromise();
  }

  createDashboardId(): string {
    return `dashboard_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  }

  createTileId(): string {
    return `tile_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  }

  createActionId(): string {
    return `action_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  }

  private normalizeDashboard(input: PayrollDashboardDefinition): PayrollDashboardDefinition {
    const now = new Date().toISOString();

    return {
      id: `${input.id || this.createDashboardId()}`,
      name: `${input.name || ''}`.trim(),
      description: `${input.description || ''}`.trim(),
      status: this.normalizeStatus(input.status),
      tiles: (Array.isArray(input.tiles) ? input.tiles : [])
        .map((tile) => this.normalizeTile(tile))
        .filter((tile): tile is PayrollDashboardTileDefinition => Boolean(tile)),
      actions: (Array.isArray(input.actions) ? input.actions : [])
        .map((action) => this.normalizeAction(action))
        .filter((action): action is PayrollDashboardActionDefinition => Boolean(action)),
      metadata: this.toObject(input.metadata),
      createdAt: input.createdAt || now,
      updatedAt: input.updatedAt || now,
      permissionCount: Number.isFinite(Number(input.permissionCount)) ? Number(input.permissionCount) : undefined,
    };
  }

  private toDashboardDefinition(input: unknown): PayrollDashboardDefinition | null {
    if (!this.isJsonObject(input)) {
      return null;
    }

    const name = `${input.name || ''}`.trim();
    if (!name) {
      return null;
    }

    const normalized = this.normalizeDashboard({
      id: `${input.id || this.createDashboardId()}`,
      name,
      description: `${input.description || ''}`.trim(),
      status: this.normalizeStatus(input.status),
      tiles: Array.isArray(input.tiles) ? input.tiles as PayrollDashboardTileDefinition[] : [],
      actions: Array.isArray(input.actions) ? input.actions as PayrollDashboardActionDefinition[] : [],
      metadata: this.toObject(input.metadata),
      createdAt: `${input.createdAt || ''}`.trim() || new Date().toISOString(),
      updatedAt: `${input.updatedAt || ''}`.trim() || new Date().toISOString(),
      permissionCount: Number.isFinite(Number(input.permissionCount)) ? Number(input.permissionCount) : undefined,
    });

    return normalized;
  }

  private extractDashboards(response: unknown): PayrollDashboardDefinition[] {
    const payload = this.isJsonObject(response) ? response : {};
    const rawDashboards = Array.isArray(payload.dashboards) ? payload.dashboards : Array.isArray(response) ? response : [];

    return rawDashboards
      .map((item: unknown) => this.toDashboardDefinition(item))
      .filter((dashboard: PayrollDashboardDefinition | null): dashboard is PayrollDashboardDefinition => Boolean(dashboard))
      .sort((left: PayrollDashboardDefinition, right: PayrollDashboardDefinition) => left.name.localeCompare(right.name));
  }

  private toDashboardPayload(dashboard: PayrollDashboardDefinition): Record<string, unknown> {
    return {
      id: dashboard.id,
      name: dashboard.name,
      description: dashboard.description || '',
      status: dashboard.status,
      tiles: dashboard.tiles,
      actions: dashboard.actions,
      metadata: this.toObject(dashboard.metadata),
      createdAt: dashboard.createdAt,
      updatedAt: dashboard.updatedAt,
    };
  }

  private toDashboardUpdatePayload(dashboard: PayrollDashboardDefinition): Record<string, unknown> {
    return {
      name: dashboard.name,
      description: dashboard.description || '',
      status: dashboard.status,
      tiles: dashboard.tiles,
      actions: dashboard.actions,
      metadata: this.toObject(dashboard.metadata),
    };
  }

  private normalizeTile(input: PayrollDashboardTileDefinition | Record<string, unknown>): PayrollDashboardTileDefinition | null {
    const value = input as Record<string, unknown>;
    const title = `${value.title || ''}`.trim();
    const reportId = `${value.reportId || ''}`.trim();
    if (!title || !reportId) {
      return null;
    }

    return {
      id: `${value.id || this.createTileId()}`,
      title,
      description: `${value.description || ''}`.trim(),
      reportId,
      metricType: this.normalizeMetricType(value.metricType),
      fieldKey: `${value.fieldKey || ''}`.trim(),
      valueFormat: this.normalizeValueFormat(value.valueFormat),
      precision: this.normalizePrecision(value.precision),
    };
  }

  private normalizeAction(input: PayrollDashboardActionDefinition | Record<string, unknown>): PayrollDashboardActionDefinition | null {
    const value = input as Record<string, unknown>;
    const label = `${value.label || ''}`.trim();
    if (!label) {
      return null;
    }

    const targetType = this.normalizeActionTargetType(value.targetType);
    const reportId = `${value.reportId || ''}`.trim();
    const templateKey = `${value.templateKey || ''}`.trim();
    if (targetType === 'saved_report' && !reportId) {
      return null;
    }
    if (targetType === 'report_template' && !templateKey) {
      return null;
    }

    return {
      id: `${value.id || this.createActionId()}`,
      label,
      description: `${value.description || ''}`.trim(),
      targetType,
      reportId,
      templateKey,
    };
  }

  private normalizeStatus(value: unknown): PayrollDashboardStatus {
    return value === 'active' || value === 'archived' ? value : 'draft';
  }

  private normalizeMetricType(value: unknown): PayrollDashboardMetricType {
    switch (value) {
      case 'sum':
      case 'average':
      case 'minimum':
      case 'maximum':
        return value;
      default:
        return 'count';
    }
  }

  private normalizeValueFormat(value: unknown): PayrollDashboardValueFormat {
    if (value === 'currency' || value === 'percent') {
      return value;
    }
    return 'number';
  }

  private normalizeActionTargetType(value: unknown): PayrollDashboardActionTargetType {
    return value === 'report_template' ? 'report_template' : 'saved_report';
  }

  private normalizePrecision(value: unknown): number {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
      return 0;
    }
    return Math.min(4, Math.max(0, Math.round(parsed)));
  }

  private toPermission(input: unknown): PayrollDashboardPermission | null {
    if (!this.isJsonObject(input)) {
      return null;
    }

    const subjectType = this.normalizePermissionSubjectType(input.subjectType);
    const subjectId = `${input.subjectId || ''}`.trim();
    if (!subjectType || !subjectId) {
      return null;
    }

    return {
      id: `${input.id || ''}`.trim() || undefined,
      dashboardId: `${input.dashboardId || ''}`.trim() || undefined,
      subjectType,
      subjectId,
    };
  }

  private toPermissionPayload(permission: PayrollDashboardPermission): { subjectType: PayrollDashboardPermissionSubjectType; subjectId: string } | null {
    const subjectType = this.normalizePermissionSubjectType(permission?.subjectType);
    const subjectId = `${permission?.subjectId || ''}`.trim();
    if (!subjectType || !subjectId) {
      return null;
    }

    return { subjectType, subjectId };
  }

  private normalizePermissionSubjectType(value: unknown): PayrollDashboardPermissionSubjectType | null {
    switch (value) {
      case 'user':
      case 'company':
      case 'team':
      case 'brand':
        return value;
      default:
        return null;
    }
  }

  private isJsonObject(value: unknown): value is Record<string, unknown> {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
  }

  private toObject(value: unknown): Record<string, unknown> {
    return this.isJsonObject(value) ? value : {};
  }
}
